import React, { useEffect, useRef, useState } from "react";
import lottie from "lottie-web";
import moment from "moment";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
} from "reactstrap";
import Dropzone from "react-dropzone";
import SweetAlert from "react-bootstrap-sweetalert";
import { CircularProgress } from "@material-ui/core";
import { FaCloudUploadAlt, FaRegTrashAlt } from "react-icons/fa";

const imageInitialState = { file: null, preview: "", deleted: false, changed: false };
const animationInitialState = { data: null, name: "", deleted: false, changed: false };

const LottiePreview = ({ animationData }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const animation = lottie.loadAnimation({
      container: containerRef.current,
      renderer: "svg",
      loop: true,
      autoplay: true,
      animationData: JSON.parse(JSON.stringify(animationData)),
    });

    return () => animation.destroy();
  }, [animationData]);

  return <div ref={containerRef} />;
};

const LoadingScreenBackground = () => {
  const [token, setToken] = useState("");
  const [assetId, setAssetId] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState(imageInitialState);
  const [backgroundImageAnimation, setBackgroundImageAnimation] = useState(animationInitialState);
  const [publishDate, setPublishDate] = useState("");
  const [unpublishDate, setUnpublishDate] = useState("");
  const [neverEnding, setNeverEnding] = useState(false);
  const [scheduleChanged, setScheduleChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successAlert, setSuccessAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const authenticationToken = localStorage.getItem("AuthenticationToken");
    setToken(authenticationToken || "");
    loadAssets(authenticationToken);
  }, []);

  const showError = (message) => setErrorMessage(message);

  const getAnimationData = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value;

    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  };

  const loadAssets = (authenticationToken) => {
    const headers = new Headers();
    headers.append("Authorization", "Bearer " + authenticationToken);

    fetch(`${process.env.REACT_APP_TOKRI_API_URL}/api/app-customization-assets`, {
      method: "GET",
      headers,
    })
      .then((response) => response.json())
      .then((result) => {
        const data = Array.isArray(result?.data) ? result.data[0] : result?.data || result;
        if (!data) return;

        setAssetId(data.id || null);
        if (data.background_image) {
          setBackgroundImage({ file: null, preview: data.background_image, deleted: false, changed: false });
        }
        if (data.background_image_animation) {
          const bgAnimationData = getAnimationData(data.background_image_animation);
          if (bgAnimationData) {
            setBackgroundImageAnimation({ data: bgAnimationData, name: "Current animation", deleted: false, changed: false });
          }
        }
        if (data.background_publish_date) {
          setPublishDate(moment(data.background_publish_date).format("YYYY-MM-DDTHH:mm"));
        }
        if (data.background_unpublish_date == null || data.background_unpublish_date === "null" || !data.background_unpublish_date) {
          setNeverEnding(true);
        } else {
          setUnpublishDate(moment(data.background_unpublish_date).format("YYYY-MM-DDTHH:mm"));
        }
      })
      .catch(() => showError("Unable to load Loading Screen Background."))
      .finally(() => setLoading(false));
  };

  const validateBackgroundImage = (file) => {
    const isSupportedImage = ["image/png", "image/svg+xml"].includes(file.type);
    if (!isSupportedImage) {
      showError("Loading Screen Background Image must be an SVG or PNG file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target.result;
      const image = new Image();
      image.onload = () => {
        if (image.naturalWidth !== 1080 || image.naturalHeight !== 1920) {
          showError("Loading Screen Background Image must be 1080 x 1920px.");
          return;
        }
        setBackgroundImage({ file, preview: dataUrl, deleted: false, changed: true });
        setBackgroundImageAnimation({ ...animationInitialState, deleted: backgroundImageAnimation.data !== null, changed: backgroundImageAnimation.data !== null });
      };
      image.onerror = () => showError("Unable to read Loading Screen Background Image.");
      image.src = dataUrl;
    };
    reader.onerror = () => showError("Unable to read Loading Screen Background Image.");
    reader.readAsDataURL(file);
  };

  const selectBackgroundImageAnimation = (file) => {
    if (file.type !== "application/json" && !file.name.toLowerCase().endsWith(".json")) {
      showError("Loading Screen Background Image animation must be a Lottie JSON file.");
      return;
    }
    if (file.size >= 400 * 1024) {
      showError("Loading Screen Background Image animation must be smaller than 400KB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        if (data.w !== 1080 || data.h !== 1920) {
          showError("Loading Screen Background Image animation must be 1080 x 1920px.");
          return;
        }
        setBackgroundImageAnimation({ data, name: file.name, deleted: false, changed: true });
        setBackgroundImage({ ...imageInitialState, deleted: backgroundImage.preview !== "", changed: backgroundImage.preview !== "" });
      } catch (error) {
        showError("Please select a valid Lottie JSON file.");
      }
    };
    reader.readAsText(file);
  };

  const removeBackgroundImage = () => {
    setBackgroundImage({ ...imageInitialState, deleted: true, changed: true });
  };

  const removeBackgroundImageAnimation = () => {
    setBackgroundImageAnimation({ ...animationInitialState, deleted: true, changed: true });
  };

  const handleSubmit = () => {
    const hasChanges = backgroundImage.changed || backgroundImageAnimation.changed || scheduleChanged;
    if (!hasChanges) {
      showError("Please upload or change at least one asset.");
      return;
    }

    if (publishDate == "Invalid date" || !publishDate) {
      showError("Please select publish date for the background image.");
      return;
    }
    if (!neverEnding) {
      if (unpublishDate == "Invalid date" || !unpublishDate) {
        showError("Please select unpublish date for the background image.");
        return;
      } else if (unpublishDate < publishDate) {
        showError("Unpublish date should be after the publish date for the background image.");
        return;
      }
    }

    const formData = new FormData();
    if (assetId) {
      formData.append("id", assetId);
      formData.append("_method", "PUT");
    }
    if (backgroundImage.file) {
      formData.append("background_image", backgroundImage.file, backgroundImage.file.name);
      formData.append("background_image_animation", "");
    } else if (backgroundImageAnimation.changed && backgroundImageAnimation.data && !backgroundImageAnimation.deleted) {
      formData.append("background_image_animation", JSON.stringify(backgroundImageAnimation.data));
      formData.append("background_image", "");
    } else {
      if (backgroundImage.deleted) formData.append("background_image", "");
      if (backgroundImageAnimation.deleted) formData.append("background_image_animation", "");
    }
    formData.append("background_publish_date", publishDate);
    formData.append("background_unpublish_date", neverEnding ? null : unpublishDate);

    const headers = new Headers();
    headers.append("Authorization", "Bearer " + token);
    setSaving(true);

    const endpoint = assetId
      ? `${process.env.REACT_APP_TOKRI_API_URL}/api/app-customization-assets/update`
      : `${process.env.REACT_APP_TOKRI_API_URL}/api/app-customization-assets`;

    fetch(endpoint, {
      method: "POST",
      headers,
      body: formData,
    })
      .then((response) => {
        if (!response.ok) throw new Error();
        return response.json().catch(() => ({}));
      })
      .then((result) => {
        const data = result?.data || result;
        setAssetId(data?.id || assetId);
        setBackgroundImage((asset) => ({ ...asset, file: null, deleted: false, changed: false }));
        setBackgroundImageAnimation((asset) => ({ ...asset, deleted: false, changed: false }));
        setScheduleChanged(false);
        setSuccessAlert(true);
      })
      .catch(() => showError("Unable to update Loading Screen Background."))
      .finally(() => setSaving(false));
  };

  const handleBackgroundFileDrop = (file) => {
    if (!file) return;
    const isJson = file.type === "application/json" || file.name.toLowerCase().endsWith(".json");
    if (isJson) {
      selectBackgroundImageAnimation(file);
    } else {
      validateBackgroundImage(file);
    }
  };

  const renderScheduleFields = () => (
    <Row className="mt-3">
      <Col lg={4} md={6} sm={6}>
        <div className="mb-3">
          <label>Publish At</label>
          <div className="col-md-12">
            <input
              className="form-control"
              type="datetime-local"
              onChange={(e) => {
                setPublishDate(moment(e.target.value).format("YYYY-MM-DDTHH:mm"));
                setScheduleChanged(true);
              }}
              value={publishDate}
            />
          </div>
        </div>
      </Col>
      <Col lg={1}></Col>
      <Col lg={4} md={6} sm={6}>
        <div className="mb-3">
          <label>Unpublish At</label>
          <div className="col-md-12">
            <input
              className="form-control"
              type="datetime-local"
              onChange={(e) => {
                setUnpublishDate(moment(e.target.value).format("YYYY-MM-DDTHH:mm"));
                setScheduleChanged(true);
              }}
              value={unpublishDate}
              disabled={neverEnding}
            />
          </div>
        </div>
      </Col>
      <Col style={{ paddingTop: 35 }}>
        <div className="form-check form-switch">
          <input
            type="checkbox"
            className="form-check-input checkbox"
            id="checkbox_never_ending_background"
            checked={neverEnding}
            onChange={() => {
              setNeverEnding(!neverEnding);
              setScheduleChanged(true);
            }}
          />
          <label className="form-check-label" htmlFor="checkbox_never_ending_background">
            Never-ending
          </label>
        </div>
      </Col>
    </Row>
  );

  if (loading) {
    return <div className="page-content text-center"><CircularProgress /></div>;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="App Customisation" breadcrumbItem="Loading Screen Background" />
        <Row>
          <Col lg={12}>
            <Card className="mb-3">
              <CardBody>
                {/* <h5>Loading Screen Background Image</h5> */}
                <p className="text-muted mb-3">1080 x 1920 · SVG, PNG or Lottie file</p>

                {backgroundImage.preview && !backgroundImage.deleted && (
                  <div className="d-flex align-items-start mb-3">
                    <img src={backgroundImage.preview} alt="Loading Screen Background" style={{ maxWidth: "180px", maxHeight: "180px", objectFit: "contain" }} />
                    <Button color="link" className="text-danger" onClick={removeBackgroundImage}>
                      <FaRegTrashAlt /> Delete
                    </Button>
                  </div>
                )}

                {backgroundImageAnimation.data && !backgroundImageAnimation.deleted && (
                  <div className="d-flex align-items-start mb-3">
                    <div style={{ width: "120px", maxWidth: "100%", aspectRatio: "1080 / 1920", overflow: "hidden" }}><LottiePreview animationData={backgroundImageAnimation.data} /></div>
                    <Button color="link" className="text-danger" onClick={removeBackgroundImageAnimation}>
                      <FaRegTrashAlt /> Delete
                    </Button>
                  </div>
                )}

                <Dropzone multiple={false} accept="image/png, image/svg+xml, application/json, .json" onDrop={(files) => handleBackgroundFileDrop(files[0])}>
                  {({ getRootProps, getInputProps }) => (
                    <div className="dropzone">
                      <div className="dz-message needsclick" {...getRootProps()}>
                        <input {...getInputProps()} />
                        <FaCloudUploadAlt className="display-4 text-muted mb-3" />
                        <h4>Drop an SVG, PNG or Lottie JSON file here or click to upload.</h4>
                      </div>
                    </div>
                  )}
                </Dropzone>
              </CardBody>
            </Card>
            <Card className="mb-3">
              <CardBody>
                {/* <h5>Schedule (Loading Screen Background Image)</h5> */}
                {renderScheduleFields()}
                <div style={{ marginTop: "1rem" }}>
                  <button
                    disabled={saving}
                    style={{ color: "#ffffff", width: "80px", marginLeft: "10px" }}
                    className={"btn btn-success"}
                    onClick={() => {
                      handleSubmit();
                    }}
                    color="success"
                    type="submit"
                  >
                    {saving ? (
                      <CircularProgress size={15} value={100} color={"white"} />
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      {successAlert && <SweetAlert title="Success!" success onConfirm={() => setSuccessAlert(false)}>Loading Screen Background updated successfully.</SweetAlert>}
      {errorMessage && <SweetAlert title="Warning!" warning onConfirm={() => setErrorMessage("")}>{errorMessage}</SweetAlert>}
    </div>
  );
};

export default LoadingScreenBackground;
